import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Plus, RefreshCw } from "lucide-react";
import { apiService } from "../../services/api";
import type { Fleet } from "../../types/auth";
import { LoadingSpinner } from "../common/LoadingSpinner";
import { FleetFilters } from "./FleetFilters";
import { FleetTable } from "./FleetTable";
import { FleetModal } from "./FleetModal";

export const FleetsList: React.FC = () => {
  const [fleets, setFleets] = useState<Fleet[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [sortBy, setSortBy] = useState("name");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<"create" | "edit">("create");
  const [selectedFleet, setSelectedFleet] = useState<Fleet | null>(null);
  const [error, setError] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(timer);
  }, [search]);

  const loadFleets = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response = await apiService.getFleets();
      setFleets(response.fleets || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load fleets");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadFleets().catch(console.error);
  }, [loadFleets]);

  const filteredAndSorted = useMemo(() => {
    const query = debouncedSearch.trim().toLowerCase();
    let list = fleets;
    if (query) {
      list = list.filter((f) => f.name.toLowerCase().includes(query));
    }
    const sorted = [...list].sort((a, b) => {
      let cmp = 0;
      if (sortBy === "name") {
        cmp = a.name.localeCompare(b.name);
      } else if (sortBy === "vehicle_count") {
        cmp = (a.vehicle_count ?? 0) - (b.vehicle_count ?? 0);
      } else if (sortBy === "created_at") {
        cmp =
          new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
      }
      return sortOrder === "asc" ? cmp : -cmp;
    });
    return sorted;
  }, [fleets, debouncedSearch, sortBy, sortOrder]);

  const handleSort = (column: string) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(column);
      setSortOrder("asc");
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Fleets</h1>
          <p className="text-gray-600 mt-2">
            Manage fleet customers and assign vehicles
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => loadFleets()}
            className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </button>
          <button
            type="button"
            onClick={() => {
              setModalMode("create");
              setSelectedFleet(null);
              setModalOpen(true);
            }}
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Fleet
          </button>
        </div>
      </div>

      {error && (
        <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">
          {error}
        </p>
      )}

      <FleetFilters search={search} onSearchChange={setSearch} />

      {loading ? (
        <LoadingSpinner />
      ) : (
        <FleetTable
          fleets={filteredAndSorted}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSort={handleSort}
          onEdit={(fleet) => {
            setModalMode("edit");
            setSelectedFleet(fleet);
            setModalOpen(true);
          }}
        />
      )}

      <FleetModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        mode={modalMode}
        fleet={selectedFleet}
        onSaved={() => loadFleets()}
      />
    </div>
  );
};
